import React from 'react';
import {
  Root as DropdownMenuRoot,
  Trigger,
  Portal,
  Group,
  Item,
} from '@radix-ui/react-dropdown-menu';
import { Theme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import palette from '@ui/palette';
import Typography from '@ui/Typography';
import Button from '@ui/Button';
import Icon, { AvailableIconTypes, IconProps } from '@ui/Icon';
import { MenuContentProps } from 'components/DropdownMenu/components/MenuContent';
import { MenuContent } from '../DropdownMenu/components';
import { alphaWhite } from '../../util/cssUtil';

export interface DropdownButtonMenuItem {
  id: string;
  label?: string;
  children?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  isSubMenu?: boolean;
  disabledTooltip?: ((children: React.ReactNode) => React.ReactNode) | null;
}

export interface DropdownButtonProps {
  id: string;
  disabled?: boolean;
  variant?: 'primary' | 'icon' | 'labeled';
  /**
   * Label of the main button as menu trigger
   */
  label?: string;
  /**
   * Optional icon to be placed before the label text
   */
  iconType?: AvailableIconTypes;
  /**
   * Set size of icon used in main button menu trigger
   */
  iconFontSize?: IconProps['fontSize'];
  /**
   * Optional class overrides
   */
  classes?: {
    menuContent?: string;
    iconButton?: string;
    labeledButton?: string;
  };
  children: Array<React.ReactElement<DropdownButtonMenuItem> | React.ReactNode>;

  portal?: boolean;

  'aria-label'?: string;
  contentProps?: Omit<MenuContentProps, 'children'>;
  tabIndex?: number;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
}

/**
 * Button with a dropdown menu to click on item which will trigger callback
 * For now, only primary button variant implemented
 * But can extend this to support other buttons as the dropdown menu trigger
 */
const DropdownButton = React.forwardRef<HTMLButtonElement, DropdownButtonProps>(
  (props, ref) => {
    const {
      id,
      disabled,
      label,
      iconType,
      variant = 'primary',
      classes: incomingClasses,
      iconFontSize,
      children,
      portal = true,
      'aria-label': ariaLabel,
      contentProps,
      tabIndex,
      onFocus,
    } = props;
    const [open, setOpen] = React.useState(false);
    const { classes, cx } = useStyles();

    const menuItems: React.ReactElement<typeof Item>[] = [];
    React.Children.forEach(children, child => {
      if (React.isValidElement(child)) {
        if (child.props.isSubMenu) {
          menuItems.push(child as React.ReactElement);
          return;
        }
        menuItems.push(
          <Item
            key={child.props.id}
            disabled={child.props.disabled}
            onSelect={child.props.onClick}
          >
            {child.props.label && (
              <Typography variant="pSm">{child.props.label}</Typography>
            )}
            {child}
          </Item>
        );
      }
    });

    return (
      <DropdownMenuRoot open={open} onOpenChange={setOpen}>
        {variant === 'icon' && iconType && (
          <Trigger asChild disabled={disabled}>
            <Button
              ref={ref}
              tabIndex={tabIndex}
              onFocus={onFocus}
              variant="icon"
              id={`${id}-button`}
              color="default"
              aria-label={ariaLabel || `Open menu`}
              data-e2e="wsl-dropdown-menu-btn"
              className={cx(classes.iconButton, incomingClasses?.iconButton, {
                [classes.open]: open,
              })}
              disabled={disabled}
            >
              <Icon color="secondary" type={iconType} fontSize={iconFontSize} />
            </Button>
          </Trigger>
        )}
        {variant === 'primary' && (
          <Trigger asChild disabled={disabled}>
            <Button
              ref={ref}
              tabIndex={tabIndex}
              onFocus={onFocus}
              variant="primary"
              id={`${id}-button`}
              className={classes.button}
              aria-label={ariaLabel || `Open menu`}
              disabled={disabled}
            >
              {iconType && <Icon type={iconType} />}
              <Typography variant="pSm" bold uppercased>
                {label}
              </Typography>
            </Button>
          </Trigger>
        )}
        {variant === 'labeled' && label && (
          <Trigger asChild disabled={disabled}>
            <Button
              ref={ref}
              tabIndex={tabIndex}
              onFocus={onFocus}
              variant="text"
              size="small"
              color="inherit"
              id={`${id}-button`}
              className={cx(
                classes.labeledButton,
                incomingClasses?.labeledButton,
                { [classes.open]: open }
              )}
              aria-label={ariaLabel || `Open ${label} menu`}
              data-e2e="wsl-dropdown-menu-btn"
              disabled={disabled}
            >
              <Typography variant="pSm" component="span">
                {label}
              </Typography>
              <Icon type="chevronDown" fontSize="small" />
            </Button>
          </Trigger>
        )}
        {portal && (
          <Portal>
            <MenuContent
              id={`${id}-menu-content`}
              align="start"
              className={cx(classes.content, incomingClasses?.menuContent)}
              {...contentProps}
            >
              <Group>{menuItems}</Group>
            </MenuContent>
          </Portal>
        )}
        {!portal && (
          <MenuContent
            id={`${id}-menu-content`}
            align="start"
            className={cx(classes.content, incomingClasses?.menuContent)}
            {...contentProps}
          >
            <Group>{menuItems}</Group>
          </MenuContent>
        )}
      </DropdownMenuRoot>
    );
  }
);

const useStyles = makeStyles()((theme: Theme) => ({
  content: {
    '& div[role="menuitem"] > *': {
      minWidth: theme.spacing(8.5),
    },
    borderTopLeftRadius: 0,
  },
  label: {
    display: 'flex',
    gap: theme.spacing(0.5),
  },
  button: {
    width: theme.spacing(12.5),
    padding: theme.spacing(1, 2),
    '&[data-state="open"]': {
      backgroundColor: theme.palette.contained.hover,
    },
    '&.Mui-focusVisible': {
      '&:not(&.keyboard-focus)': {
        outline: 'none',
        backgroundColor: theme.palette.primary.main,
      },
    },
  },
  iconButton: {
    borderRadius: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: alphaWhite(0.12),
    },
    '&:focus-visible, &.Mui-focusVisible': {
      outline: `2px solid ${palette.mint_200}`,
      outlineOffset: -2,
    },
  },
  open: {
    backgroundColor: alphaWhite(0.09),
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  labeledButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.spacing(0.75),
    backgroundColor: 'transparent',
    color: palette.gray_50,
    textTransform: 'none',
    minHeight: 'auto',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&[data-state="open"]': {
      backgroundColor: theme.palette.action.hover,
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
    '&:focus-visible, &.Mui-focusVisible': {
      outline: `2px solid ${palette.mint_200}`,
      outlineOffset: -2,
    },
  },
}));

export default DropdownButton;
